import * as React from 'react'
import { EventType, EventTypeColumnConfig, EventTypeColumnProperty, EventTypeColumns, EventTypeProperty, ResourceRendererOptions, ResourceRendererResult } from '../../types'
import { ResourceTableHeader } from './ResourceTable'
import ResourceTableRow from './ResourceTableRow'
import ResourceTableRowData from './ResourceTableRowData'
import ResourceTableRowActions from './ResourceTableRowActions'
import { IActionListItem } from '../ActionList/ActionList'
import i18n from '../../translations'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import moment from '../../utilities/Moment'
import styled from 'styled-components'
import Badge from '../Badge/Badge'
import Avatar from '../Avatar/Avatar'
import { Link } from 'react-router-dom'
import RouteHelper from '../../helpers/RouteHelper'
import ERoute from '../../ERoute'
import { locationLabel } from '../Scheduling/Booker/format'

const EventTypeNameAndMetadata = styled.div`
  display: flex;
  flex-direction: column;
`

const EventTypeName = styled(Link)`
  font-weight: 500;
  color: black;

  &:hover {
    text-decoration: underline;
  }
`

const EventTypeMetadata = styled.div`
  color: #637381;
  font-size: 13px;
`

const BadgeList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 4px;
`

const HostContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

export const DEFAULT_EVENT_TYPE_COLUMNS: EventTypeColumnProperty[] = [
  'title',
  'host',
  'durations',
  'active',
]

const EventTypeColumnConfigs: Record<EventTypeColumnProperty, EventTypeColumnConfig> = {
  title: {
    header: () => ({ title: i18n.t('EventType::title') }),
    render: ({ eventType }) => (
      <EventTypeNameAndMetadata>
        <EventTypeName
          to={RouteHelper.process(ERoute.PATH_SCHEDULING_EVENT_TYPE, { id: eventType.id })}
          onClick={(e) => e.stopPropagation()}
        >
          {eventType.title}
        </EventTypeName>
        <EventTypeMetadata>/{eventType.booking_slug}/{eventType.slug}</EventTypeMetadata>
      </EventTypeNameAndMetadata>
    ),
  },
  durations: {
    header: () => ({ title: i18n.t('EventType::durations') }),
    render: ({ eventType }) => {
      const durations = eventType.durations.length > 0 ? eventType.durations : [eventType.duration]

      return (
        <BadgeList>
          {durations.map(duration => <Badge key={duration} type='grey' text={i18n.t('EventTypes::{{count}}m', { count: duration })} />)}
        </BadgeList>
      )
    },
  },
  locations: {
    header: () => ({ title: i18n.t('EventType::locations') }),
    render: ({ eventType }) => {
      if (!eventType.locations || eventType.locations.length === 0) return '-'

      return (
        <BadgeList>
          {eventType.locations.map(location => <Badge key={location.type} type='grey' text={locationLabel(i18n.t, location.type)} />)}
        </BadgeList>
      )
    },
  },
  active: {
    header: () => ({ title: i18n.t('EventType::active') }),
    render: ({ eventType }) => eventType.active ? i18n.t('Yes') : i18n.t('No'),
  },
  host: {
    header: () => ({ title: i18n.t('EventType::host') }),
    render: ({ eventType }) => {
      if (!eventType.host) return '-'

      return (
        <HostContainer>
          <Avatar name={eventType.host.name} width={24} rounded />
          {eventType.host.name}
        </HostContainer>
      )
    },
  },
  created_at: {
    header: () => ({ title: i18n.t('EventType::created_at') }),
    render: ({ eventType, setting }) => eventType.created_at ? moment(eventType.created_at).format(setting.date_format) : '-',
  },
}

export function getEventTypeTableRenderer(options: ResourceRendererOptions<EventType>): ResourceRendererResult<EventType, EventTypeProperty> {
  const {
    onRowClick,
    selectedItems = [],
    onSelectionChange,
    onActionClick
  } = options

  const columns: { label: string, value: string }[] = EventTypeColumns.map(column => ({ label: i18n.t(`EventType::${column}`), value: column }))
  const selectedColumns: EventTypeColumnProperty[] = LocalStorage
    .get(LocalStorageKey.EVENT_TYPE_COLUMNS, DEFAULT_EVENT_TYPE_COLUMNS)
    .filter((column) => EventTypeColumnConfigs[column])

  const headers: ResourceTableHeader[] = selectedColumns
    .map(column => ({
      ...EventTypeColumnConfigs[column].header(),
      visible: true
    }))

  headers.push({ title: '', stickyRight: '0px' })

  const renderRow = (eventType: EventType, _index: number): React.ReactElement => {
    const isSelected = selectedItems.includes(eventType.id)
    const actions: IActionListItem[] = [
      ...(eventType.public_url ? [
        { key: 'preview', icon: 'external-link', content: i18n.t('EventTypesTable::Preview') },
        { key: 'copy_link', icon: 'link', content: i18n.t('EventTypesTable::Copy link') },
      ] as IActionListItem[] : []),
      { key: 'toggle_active', icon: eventType.active ? 'pause' : 'check', content: eventType.active ? i18n.t('EventTypesTable::Deactivate') : i18n.t('EventTypesTable::Activate') },
      { key: 'duplicate', icon: 'duplicate', content: i18n.t('EventTypesTable::Duplicate') },
      { key: 'edit', icon: 'edit-solid', content: i18n.t('EventTypesTable::Edit') },
      { key: 'delete', icon: 'trash-alt-solid', content: i18n.t('EventTypesTable::Delete'), destructive: true }
    ]

    let cells: React.ReactNode[] = selectedColumns.map((column) => (
      <ResourceTableRowData
        key={`${eventType.id}-${column}`}
        onClick={onRowClick ? () => onRowClick(eventType) : undefined}
        maxWidth={column === 'title' ? '300px' : undefined}
        ellipse={column === 'title'}
        textAlign={EventTypeColumnConfigs[column].header().align}
      >
        {EventTypeColumnConfigs[column].render({ eventType, ...options })}
      </ResourceTableRowData>
    ))

    if (onActionClick) {
      cells = [
        ...cells,
        <ResourceTableRowActions
          key={`${eventType.id}-actions`}
          actions={actions}
          onActionClick={(key) => onActionClick(key, eventType)}
          sticky={true}
          stickyRight='0px'
        />
      ]
    }

    return (
      <ResourceTableRow
        key={eventType.id}
        selected={isSelected}
        onSelectionChange={onSelectionChange ? (selected) => onSelectionChange(selected, eventType.id) : undefined}
      >
        {cells}
      </ResourceTableRow>
    )
  }

  return {
    headers: headers,
    columns: columns,
    selectedColumns: selectedColumns,
    renderRow: renderRow,
  }
}

export default getEventTypeTableRenderer
